import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import i18n from 'cityads-v3/i18n';
import classNames from 'classnames';
import { Helmet } from 'react-helmet';
import { Route, Redirect } from 'react-router-dom';
import { concat } from 'cityads-v3/utils/arrayUtils';
import { parseNewsItem, getNewsFields, extractImgSrcFromHTML } from 'cityads-v3/utils/newsUtils';

import MediaQuery from 'react-responsive';
import InfiniteScroll from 'react-infinite-scroller';
import CircularProgress from '@mui/material/CircularProgress'
import Balaclava from 'cityads-v3/components/Balaclava';
import NewsCard from './NewsCard';

import { clearNews, getNews, getNewsById } from 'cityads-v3/actions/news';

import classes from './News.scss';

@connect(
  (state, props) => {
    const newsIdsByTypeId = state.news.newsIdsByTypeId;
    const newsById = state.news.newsById;
    const language = state.language.language;
    const matchSection = props.match.params.type;

    const newsSections = state.news.newsSections[language] || state.news.newsSections.default;
    const currentSection = newsSections.includes(matchSection) ? state.news.newsSectionsByName[matchSection] : null;

    if (currentSection) {
      currentSection.types = currentSection.types[language] || currentSection.types.default || currentSection.types;
    }

    const currentListIds = currentSection ?
      concat(...currentSection.types.map(typeId => newsIdsByTypeId[typeId] || [])) :
      [];
    const currentList = currentListIds
      .map(item => parseNewsItem(newsById[item]))
      .sort((n1, n2) => (new Date(n2.date_publish) - new Date(n1.date_publish)) || (n2.id - n1.id)) // sort desc
    ;

    return {
      language,

      newsSections,
      newsSectionsByName: state.news.newsSectionsByName,
      currentSection,

      newsIdsByTypeIdLoading: state.news.newsIdsByTypeIdLoading,
      newsIdsByTypeIdLoaded : state.news.newsIdsByTypeIdLoaded,
      newsIdsByTypeIdErrored: state.news.newsIdsByTypeIdErrored,
      newsIdsByTypeId,

      newsById,
      isLoadingNews: state.news.isLoadingNews,
      isNewsLoaded : state.news.isNewsLoaded,
      newsPage     : state.news.newsPage,
      newsTotal    : state.news.newsTotal,

      currentList,
      currentListIds
    };
  }
)
@withTranslation(['News', 'main'], { i18n, wait: !!window })
export default class News extends PureComponent {
  static propTypes = {
    dispatch: PropTypes.func,
    history : PropTypes.object,
    t       : PropTypes.func,
    language: PropTypes.string,
    newsPage: PropTypes.number,
    newsTotal:PropTypes.number,
  }

  UNSAFE_componentWillMount() {
    const { dispatch, i18n, language, currentSection, newsIdsByTypeIdLoading, newsIdsByTypeIdLoaded, newsIdsByTypeIdErrored } = this.props;

    i18n.reloadResources(language, ['News']);

    if (currentSection) {
      const joinedTypes = currentSection.types.join(',');

      if (
        !newsIdsByTypeIdLoading[joinedTypes]
        && !newsIdsByTypeIdLoaded[joinedTypes]
        && (!newsIdsByTypeIdErrored[joinedTypes] || typeof window !== 'undefined')
      ) {
        dispatch(getNews(currentSection.types, language));
      }
    }
  }

  componentDidMount() {
    this.jumpToFirstSectionIfNeeded();
  }

  componentDidUpdate(prevProps) {
    const {
      dispatch,
      language,
      currentSection,
      newsIdsByTypeIdLoading,
      newsIdsByTypeIdLoaded,
    } = this.props;
    const {
      language: prevLanguage,
      currentSection: prevCurrentSection,
    } = prevProps;

    if (
      currentSection &&
      (currentSection !== prevCurrentSection || language !== prevLanguage)
    ) {
      const joinedTypes = currentSection.types.join(',');

      if (language !== prevLanguage) {
        dispatch(clearNews());
      }

      if ((!newsIdsByTypeIdLoading[joinedTypes] && !newsIdsByTypeIdLoaded[joinedTypes]) || (language !== prevLanguage)) {
        dispatch(getNews(currentSection.types, language));
      }
    }

    this.jumpToFirstSectionIfNeeded();
  }

  jumpToFirstSectionIfNeeded() {
    const { history, newsSections, currentSection } = this.props;

    if (!currentSection) {
      history.push(`/main/news/${newsSections[0]}`);
    }
  }

  loadPage(page = 1, loadMore = false) {
    const { currentSection, language, dispatch } = this.props;

    if (currentSection) {
      dispatch(getNews(currentSection.types, language, {
        page,
        loadMore
      }));
    }
  }

  loadNextPage() {
    const { newsPage, currentList, newsTotal } = this.props;

    if (currentList.length < newsTotal) {
      this.loadPage(newsPage + 1, true);
    }

  }

  renderItem(item) {
    const { t, location } = this.props;
    return (
      <NewsCard
        className={classes.newsList__item}
        cardHeaderClasses={{ root: classes.newsItemHeader }}
        key={item.id}
        id={+item.id}
        match={this.props.match}
        {...getNewsFields(item, t, location.pathname)}
      />
    );
  }

  renderCurrentList() {
    const { currentList, newsTotal, location, newsById } = this.props;
    const left = [];
    const right = [];
    const currentId = +location.pathname.split(/main\/news\/([a-z_]+)\/([0-9]+)/)[2];

    if(currentId && currentList.length && currentList.filter(item => +item.id === currentId).length === 0 && newsById[currentId]) {
      currentList.push(parseNewsItem(newsById[currentId]));
    }

    currentList.forEach((item, i) => {
      const node = this.renderItem(item);
      if (i % 2 === 0){
        left.push(node);
      } else {
        right.push(node);
      }
    });

    return (
      <InfiniteScroll
        threshold={400}
        loadMore={() => {
          this.loadNextPage();
        }}
        hasMore={!this.props.isLoadingNews && newsTotal > currentList.length}
        initialLoad={false}
        loader={<div className={classes.newsList__loading} key={0}>Loading ...</div>}
      >
        <MediaQuery key="start" minWidth={868} device={window ? null : { width: 1024 }}>
          {(matches) => (
            matches ? (
              <div className={classes.newsColList}>
                <div className={classNames(classes.newsColList__side, classes.newsColList__side_left)}>{left}</div>
                <div className={classNames(classes.newsColList__side, classes.newsColList__side_right)}>{right}</div>
              </div>
            ) : (
              <div className={classes.newsList}>
                {currentList.map(item => this.renderItem(item))}
              </div>
            )
          )}
        </MediaQuery>

      </InfiniteScroll>
    );
  }

  render() {
    const { t, currentSection, newsIdsByTypeIdLoading, newsSections, newsSectionsByName } = this.props;

    return (
      <>
        <Helmet>
          <title>{(currentSection ? t(`types.${currentSection.name}`) : t('title')).replace(/­/g, '')}</title>
          <meta name="description" content={t('description')}/>
          <meta name="keywords" content={t('keywords')}/>
        </Helmet>

        <Balaclava
          slogan = {t('main:slogan')}
          menuShow  = {true}
          menuValue = {currentSection ? currentSection.name : null}
          menu      = {
            newsSections
              .map(sectionName => newsSectionsByName[sectionName])
              .filter(newsSection => !!newsSection)
              .map(({ name }) => ({
                id: name,
                title: t(`News:types.${name}`),
                route: `/main/news/${name}`,
              }))
          }
        />

        <Route strict exact path="/main/news/:type/" render={({ match }) => <Redirect to={`/main/news/${match.params.type}`} /> } />
        <Route strict path={['/main/news/:type/:code([0-9]+)/','/main/news/:type/:code([0-9]+-[0-9a-z\\-]*)/']} render={({ match }) => (
          <Redirect to={`/main/news/${match.params.type}/${match.params.code}`} />
        )} />

        <Route path="/main/news/:type?/:newsId" component={NewsMeta} />

        <div className={classes.news}>
          {this.renderCurrentList()}
          {(currentSection && newsIdsByTypeIdLoading[currentSection.types.join(',')]) ?
            (
              <div className={classes.loading}><CircularProgress size={40} /></div>
            ) :
            null
          }
        </div>
      </>
    );
  }
}

@connect(
  (state, props) => {
    return {
      currentNew: state.news.newsById[parseInt(props.match.params.newsId, 10)] || {},
      isNewsByIdFailed: state.news.isNewsByIdFailed,
    }
  }
)
export class NewsMeta extends PureComponent {

  static props = {
    currentNew: PropTypes.object,
    isNewsByIdFailed: PropTypes.bool,
  }

  UNSAFE_componentWillMount() {
    const { id } = this.props.currentNew;
    const { newsId } = this.props.match.params;
    const newsIdNumber = parseInt(newsId, 10);

    if (!this.props.isNewsByIdFailed && (!id || parseInt(id, 10) !== newsIdNumber) && !isNaN(newsIdNumber)) {
      this.props.dispatch(getNewsById(newsIdNumber));
    }
  }

  render() {
    const { title, date_publish, body } = this.props.currentNew;
    const image = extractImgSrcFromHTML(body);

    return (
      <>
        {title || image ? (
          <Helmet>
            <meta property="og:title" content={title}/>
            {image ? <meta property="og:image" content={'http://cityads.com/' + image}/> : false}
            {image ? <meta property="og:image:secure_url" content={'https://cityads.com/' + image}/> : false}
            <meta property="og:url" content={'https://cityads.com/main/news/all/' + this.props.match.params.newsId}/>
            <meta property="og:type" content="article"/>
            <meta property="article:published_time" content={date_publish}/>
            <title>{title}</title>
          </Helmet>
        ) : false}
      </>
    )
  }
}
